import get from 'lodash/get';
import { Subscribe } from 'unstated';

import ContentBox from '~/components/ContentBox';
import GraphqlError from '~/components/GraphqlError';
import LoadingArea from '~/components/LoadingArea';
import MissingPage from '~/components/MissingPage';
import SegmentLayout from '~/components/SegmentLayout';
import UserContainer from '~/containers/User';
import { audienceSubtypeEnabled, audienceSubtypes } from '~/lib/audiences';
import {
  alertDestinationTypes,
  personalizationDestinationTypes,
} from '~/lib/destinations';
import { useFancyQuery } from '~/lib/graphql';
import { paginateData } from '~/lib/graphql/pagination';
import { readableSingularSegmentType, segmentTypes } from '~/lib/segments';

import AddPlan from './AddPlan';
import Audience from './Audience';
import Destination from './Destination';
import {
  AddAudience,
  AddDestination,
} from './Destination/AddDestinationOrAudience';
import query from './query.graphql';
import styles from './styles';

const audienceSubTypesToShowAsDestinations = {
  [segmentTypes.company]: [
    audienceSubtypes.companySegmentProspects,
    audienceSubtypes.contacts,
    audienceSubtypes.visitors,
  ],
  [segmentTypes.person]: [audienceSubtypes.contacts],
};

export function SegmentDestinationsPage({
  classes,
  data,
  userContainer,
  error,
  refetch,
  loading,
}) {
  const { account, segment, slackAccountConnector } = data;

  // Loading state
  if (loading && !segment?.id) return <LoadingArea loading />;

  // Total counts
  const { destinationsCount = 0, destinations } = segment;
  const { audiences, audiencesTotalCount = 0 } = paginateData(segment, {
    fields: ['audiences'],
  });

  const destinationsAndAudiencesCount = destinationsCount + audiencesTotalCount;

  // Showing and hiding sections
  const showDestinations = destinationsAndAudiencesCount > 0;
  const showAlerting = userContainer.shouldShowDestinationGroup('alerting');
  const showAudiences = userContainer.shouldShowDestinationGroup('ads');
  const showPersonalization = userContainer.shouldShowDestinationGroup(
    'personalization',
  );

  return (
    <div className={classes.root}>
      <h2 className={classes.heading}>Set up a new destination</h2>
      <p className={classes.infoText}>
        Automatically trigger an action when a{' '}
        {readableSingularSegmentType(segment.type)} matches a segment.{' '}
        <a
          className={classes.link}
          target="_blank"
          rel="noopener noreferrer"
          href="https://help.clearbit.com/hc/en-us/articles/360023392714">
          Learn more
        </a>
      </p>
      <ContentBox>
        <GraphqlError inline error={error} refetch={refetch} />

        {showDestinations && (
          <>
            <ContentBox.Title>
              <h2 className={classes.heading}>Configured destinations</h2>
            </ContentBox.Title>

            {!destinations ? (
              new Array(destinationsAndAudiencesCount)
                .fill({})
                .map((_empty, index) => (
                  <Destination
                    className={classes.audienceItem}
                    key={`loading-${index}`}
                    loading
                    destination={{}}
                  />
                ))
            ) : (
              <>
                {audiences?.map((audience) => (
                  <Audience
                    className={classes.audienceItem}
                    audience={audience}
                    key={audience.id}
                  />
                ))}
                {destinations?.map((destination) => (
                  <Destination
                    segment={segment}
                    className={classes.audienceItem}
                    key={destination.id}
                    loading={!destination.config}
                    destination={destination}
                    slackAccountConnector={slackAccountConnector}
                  />
                ))}
              </>
            )}
            <ContentBox.Break className={classes.break} />
          </>
        )}

        {showAlerting ? (
          <>
            <ContentBox.Title>
              <h2 className={classes.heading}>Alerting and notifications</h2>
              <p className={classes.infoText}>
                Send alerts and notifications to your teams.
              </p>
            </ContentBox.Title>
            {alertDestinationTypes(userContainer, segment).map((type) => (
              <AddDestination
                key={type}
                className={classes.audienceItem}
                type={type}
                segment={segment}
              />
            ))}
          </>
        ) : (
          <AddPlanSection title="Alerting" />
        )}

        <ContentBox.Break className={classes.break} />

        {showAudiences ? (
          <>
            <ContentBox.Title>
              <h2 className={classes.heading}>Advertising Audiences</h2>
              <p className={classes.infoText}>
                Send segment matches to Facebook for effective, targeted
                advertising.
              </p>
            </ContentBox.Title>
            {audienceSubTypesToShowAsDestinations[segment.type]?.map(
              (audienceSubtype) =>
                audienceSubtypeEnabled(account?.plans, audienceSubtype) && (
                  <AddAudience
                    key={audienceSubtype}
                    className={classes.audienceItem}
                    subtype={audienceSubtype}
                    segment={segment}
                  />
                ),
            )}
          </>
        ) : (
          <AddPlanSection title="Advertising" />
        )}

        {segment.type == segmentTypes.company && (
          <>
            <ContentBox.Break className={classes.break} />
            {showPersonalization ? (
              <>
                <ContentBox.Title>
                  <h2 className={classes.heading}>Personalization</h2>
                  <p className={classes.infoText}>
                    Customize your website in realtime.
                  </p>
                </ContentBox.Title>

                {personalizationDestinationTypes(userContainer, segment).map(
                  (type) => (
                    <AddDestination
                      key={type}
                      className={classes.audienceItem}
                      type={type}
                      segment={segment}
                    />
                  ),
                )}
              </>
            ) : (
              <AddPlanSection title="Personalization" />
            )}
          </>
        )}
      </ContentBox>
    </div>
  );
}

const WrappedSegmentDestinationsPage = (props) => {
  const graphqlProps = useFancyQuery(query, {
    returnPartialData: true,
    variables: { segmentId: props.segmentId, segmentType: props.segmentType },
    fields: ['audiences'],
  });

  const {
    data: { segment },
    loading,
  } = graphqlProps;

  if (!loading && !segment?.id) {
    return <MissingPage />;
  }

  if (loading && !segment) return <LoadingArea />;

  return (
    <SegmentLayout
      tab="destinations"
      segment={segment}
      resultsCount={get(segment, 'resultsCount')}>
      <Subscribe to={[UserContainer]}>
        {(userContainer) => (
          <SegmentDestinationsPage
            {...graphqlProps}
            {...props}
            userContainer={userContainer}
          />
        )}
      </Subscribe>
    </SegmentLayout>
  );
};

WrappedSegmentDestinationsPage.propTypes = {
  segmentId: PropTypes.string,
  segmentType: PropTypes.string,
};

SegmentDestinationsPage.propTypes = {
  classes: PropTypes.object.isRequired,
  data: PropTypes.shape({
    account: PropTypes.shape({
      plans: PropTypes.arrayOf(PropTypes.string),
    }),
    audiences: PropTypes.array,
    audiencesTotalCount: PropTypes.number,
    slackAccountConnector: PropTypes.shape({
      remoteMeta: PropTypes.shape({
        users: PropTypes.array,
        channels: PropTypes.array,
      }),
    }),
    segment: PropTypes.shape({
      id: PropTypes.string,
      audiences: PropTypes.object,
      destinations: PropTypes.array,
      destinationsCount: PropTypes.number,
      type: PropTypes.oneOf([segmentTypes.company, segmentTypes.person]),
    }),
  }),
  error: PropTypes.object,
  refetch: PropTypes.func,
  loading: PropTypes.bool,
  segmentId: PropTypes.string,
  segmentType: PropTypes.string,
  userContainer: PropTypes.object.isRequired,
};

export default withStyles(styles)(WrappedSegmentDestinationsPage);

const AddPlanSection = ({ title }) => (
  <ContentBox.Group>
    <ContentBox.Section>
      <AddPlan title={title} />
    </ContentBox.Section>
  </ContentBox.Group>
);

AddPlanSection.propTypes = {
  title: PropTypes.string,
  types: PropTypes.arrayOf(PropTypes.string),
};
